import { useCallback, useMemo, useState } from 'react';
import { Button, message, Modal, Tag } from 'antd';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import NewButton from '../../../components/NewButton/NewButton';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import { deletePolicy, getAllPolicies, uploadPolicyExtraction } from '../../../infrastructure/apiClient';
import type { PolicyExtractionDTO } from '../../../infrastructure/models';
import CardTable from '../../../components/Table/CardTable';
import { buildFilterOptions, getCheckboxFilterProps } from '../../../components/Table/columnFilterUtils';
import PolicyUpload, { type PolicyUploadPayload } from '../PolicyUpload/PolicyUpload';
import DashboardLayout, { dashboardStyles as styles } from '../../../components/DashboardLayout/DashboardLayout';
import { getNextPageAfterDelete } from '../../../utils/pagination';
import { useDashboardData } from '../../../hooks/useDashboardData';
import { useDeleteConfirmation } from '../../../hooks/useDeleteConfirmation';
import { sortData, sortConfig, type SortConfig } from '../../../utils/sortUtils';

interface PolicyDashboardProps {
  onPolicySelect?: (policy: PolicyExtractionDTO) => void;
}

const STATUS_COLORS: Record<string, string> = {
  Pending: 'gold',
  Completed: 'green',
  Failed: 'red',
};

const SORT_CONFIGS: SortConfig<PolicyExtractionDTO>[] = [
  sortConfig('location', 'string'),
  sortConfig('status', 'string'),
  sortConfig('userId', 'string'),
  sortConfig('version', 'number'),
  sortConfig('createdBy', 'string'),
  sortConfig('createdAt', 'date'),
  sortConfig('updatedAt', 'date'),
];

const PolicyDashboard = ({ onPolicySelect }: PolicyDashboardProps) => {
  const {
    data: policies,
    loading,
    setLoading,
    error,
    activeClient,
    tenantsLoading,
    pagination,
    sorter,
    tablePagination,
    handleTableChange,
    updateCurrentPage,
    fetchData: fetchPolicies,
    abortControllerRef,
    disableActions,
  } = useDashboardData<PolicyExtractionDTO>({
    fetchFn: getAllPolicies,
    entityName: 'policies',
  });
  const [policyUploadModalOpen, setPolicyUploadModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [locationFilters, setLocationFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [createdByFilters, setCreatedByFilters] = useState<string[]>([]);

  const handleDeletePolicy = useCallback(async (policy: PolicyExtractionDTO) => {
    if (!activeClient?.id) {
      message.warning('Select a tenant before deleting a policy.');
      return;
    }

    try {
      setLoading(true);
      await deletePolicy(activeClient.id, policy.id);
      const { nextPage, offset } = getNextPageAfterDelete({
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        totalItems: pagination.total,
        deletedItems: 1,
      });
      updateCurrentPage(nextPage);
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      await fetchPolicies(activeClient.id, { offset, limit: pagination.pageSize, signal: controller.signal });
      message.success(`Policy ${policy.location} deleted.`);
    } catch (err) {
      console.error('Failed to delete policy', err);
      message.error('Failed to delete policy.');
    } finally {
      setLoading(false);
    }
  }, [activeClient?.id, fetchPolicies, pagination.current, pagination.pageSize, pagination.total, updateCurrentPage]);

  const {
    confirmOpen,
    confirmLoading,
    itemToDelete: confirmPolicy,
    requestDelete: handleOpenDeleteConfirm,
    cancelDelete: handleCloseDeleteConfirm,
    confirmDelete: handleConfirmDelete,
  } = useDeleteConfirmation<PolicyExtractionDTO>(handleDeletePolicy);

  const locationOptions = useMemo(() => buildFilterOptions(policies, (r) => r.location ?? undefined), [policies]);
  const statusOptions = useMemo(() => buildFilterOptions(policies, (r) => r.status ?? undefined), [policies]);
  const createdByOptions = useMemo(() => buildFilterOptions(policies, (r) => r.createdBy ?? undefined), [policies]);

  const columns = useMemo<ColumnsType<PolicyExtractionDTO>>(() => [
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      ellipsis: true,
      sorter: (a, b) => a.location.localeCompare(b.location),
      sortOrder: sorter.columnKey === 'location' ? sorter.order : null,
      ...getCheckboxFilterProps(locationFilters, setLocationFilters, locationOptions, (r) => r.location ?? undefined),
      render: (value: string, record) => {
        const status = (record.status || '').toLowerCase();
        const isPending = status === 'pending';
        if (isPending || !onPolicySelect) {
          return value;
        }
        return (
          <button
            type="button"
            className={styles.nameLink}
            onClick={() => onPolicySelect(record)}
          >
            {value}
          </button>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
      sortOrder: sorter.columnKey === 'status' ? sorter.order : null,
      ...getCheckboxFilterProps(statusFilters, setStatusFilters, statusOptions, (r) => r.status ?? undefined),
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] || 'default'} className="!mr-0">
          {status}
        </Tag>
      ),
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      align: 'right',
      width: 120,
      sorter: (a, b) => a.version - b.version,
      sortOrder: sorter.columnKey === 'version' ? sorter.order : null,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => dayjs(value).format('MMM D, YYYY h:mm A'),
      width: 200,
      sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
      sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : null,
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value: string) => dayjs(value).format('MMM D, YYYY h:mm A'),
      width: 200,
      sorter: (a, b) => dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf(),
      sortOrder: sorter.columnKey === 'updatedAt' ? sorter.order : null,
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      ellipsis: true,
      sorter: (a, b) => a.createdBy.localeCompare(b.createdBy),
      sortOrder: sorter.columnKey === 'createdBy' ? sorter.order : null,
      ...getCheckboxFilterProps(createdByFilters, setCreatedByFilters, createdByOptions, (r) => r.createdBy ?? undefined),
    },
    {
      title: 'Delete',
      key: 'delete',
      align: 'center',
      width: 80,
      render: (_value, record) => (
        <button
          type="button"
          className={styles.deleteButton}
          aria-label="Delete policy"
          onClick={() => handleOpenDeleteConfirm(record)}
          disabled={loading || tenantsLoading || !activeClient?.id}
        >
          <DeleteOutlined />
        </button>
      ),
    },
  ], [sorter.columnKey, sorter.order, onPolicySelect, handleDeletePolicy, loading, tenantsLoading, activeClient?.id,
    locationFilters, locationOptions, statusFilters, statusOptions, createdByFilters, createdByOptions]);

  const sortedPolicies = useMemo(() => sortData(policies, sorter, SORT_CONFIGS), [policies, sorter]);

  const handleNewExtraction = () => {
    setPolicyUploadModalOpen(true);
  };

  const handleClosePolicyUploadModal = () => {
    setPolicyUploadModalOpen(false);
  };

  const handlePolicyUploadSubmit = async ({ location, files }: PolicyUploadPayload) => {
    if (!activeClient?.id) {
      message.warning('Select a tenant before submitting a policy extraction request.');
      return;
    }

    const tenantId = activeClient.id;

    if (!files.length) {
      message.warning('Select a document before uploading.');
      return;
    }

    try {
      setLoading(true);
      await uploadPolicyExtraction(tenantId, location, files);
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      await fetchPolicies(tenantId, { offset: 0, limit: pagination.pageSize, signal: controller.signal });
      const successMessage = files.length === 1
        ? `Policy extraction request submitted for ${location}.`
        : `${files.length} policy extraction requests submitted for ${location}.`;
      message.success(successMessage);
      setPolicyUploadModalOpen(false);
    } catch (err) {
      console.error('Failed to submit policy extraction request', err);
      message.error('Failed to submit policy extraction request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      title="Policy Extraction"
      actions={
        <>
          <NewButton onClick={handleNewExtraction} disabled={disableActions} label="New Policy Extraction" />
          <Button
            icon={<ReloadOutlined />}
            onClick={async () => {
              if (!activeClient?.id) return;
              abortControllerRef.current?.abort();
              const controller = new AbortController();
              abortControllerRef.current = controller;
              setRefreshing(true);
              try {
                await fetchPolicies(activeClient.id, {
                  offset: 0,
                  limit: pagination.pageSize,
                  signal: controller.signal,
                });
              } finally {
                if (!controller.signal.aborted) {
                  setRefreshing(false);
                }
              }
            }}
            disabled={disableActions || refreshing}
            loading={refreshing}
          >
            Refresh
          </Button>
        </>
      }
    >
      <CardTable<PolicyExtractionDTO>
        title="Existing Policies"
        columns={columns}
        dataSource={sortedPolicies}
        rowKey={(record) => record.id}
        loading={loading || tenantsLoading}
        pagination={tablePagination}
        onChange={handleTableChange}
        error={error}
        emptyText={disableActions ? 'Select a tenant to view policies.' : 'No policies found yet.'}
        className={styles.cardTable}
      />

      <Modal
        open={policyUploadModalOpen}
        title="New Policy Extraction"
        onCancel={handleClosePolicyUploadModal}
        footer={null}
        destroyOnHidden
        width={640}
        styles={{ body: { minHeight: 360 } }}
      >
        <PolicyUpload onSubmit={handlePolicyUploadSubmit} />
      </Modal>
      <ConfirmDialog
        open={confirmOpen}
        title="Delete policy"
        message={confirmPolicy ? `Are you sure you want to delete the policy ${confirmPolicy.location}?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        confirmLoading={confirmLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteConfirm}
      />
    </DashboardLayout>
  );
};

export default PolicyDashboard;
